import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AnDateField from '../../../components/DateTime/DateField'
import { GlobalContext } from '../../../App'

// Extension + upload Content-Type per accepted MIME type. Keys double as the
// whitelist; extensions must stay within contentTypeFromExtension() in
// server/src/helpers/s3.js so viewing serves the right type.
const SOP_FILE_TYPES = {
  'application/pdf': { extension: 'pdf', contentType: 'application/pdf' },
  'video/mp4': { extension: 'mp4', contentType: 'video/mp4' },
  'video/x-m4v': { extension: 'mp4', contentType: 'video/mp4' },
  'video/quicktime': { extension: 'mov', contentType: 'video/quicktime' },
  'video/webm': { extension: 'webm', contentType: 'video/webm' },
}

const SOP_MAX_FILE_BYTES = 1024 * 1024 * 1024 // 1GB

const AddNewSOP = ({ loadSOPs, onClose, existingSOP, onCloseSubmit }) => {
  const [title, setTitle] = useState(existingSOP?.title ?? '')
  const [type, setType] = useState(existingSOP?.type ?? '')
  const [version, setVersion] = useState(existingSOP?.version ?? '')
  const [sopNumber, setSopNumber] = useState(existingSOP?.sopNumber ?? '')
  const [date, setDate] = useState(existingSOP?.date ?? new Date())
  const [faoRoles, setFAORoles] = useState(existingSOP?.faoRoles ?? [])
  const [faoAllStaff, setFAOAllStaff] = useState(
    existingSOP?.faoAllStaff === true ? true : false
  )
  const [reviewDate, setReviewDate] = useState(existingSOP?.reviewDate ?? '')
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const [roleOptions, setRoleOptions] = useState([])
  const { setToast } = useContext(GlobalContext)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    if (!SOP_FILE_TYPES[selectedFile.type]) {
      // Clear the native input too, or `required` treats the rejected file
      // as a valid selection.
      e.target.value = ''
      setFile(null)
      setToast({
        message: 'SOP attachments must be a PDF or a video (MP4, MOV, WebM).',
        severity: 'error',
      })
      return
    }
    if (selectedFile.size > SOP_MAX_FILE_BYTES) {
      e.target.value = ''
      setFile(null)
      setToast({
        message: 'SOP attachments must be 1GB or smaller.',
        severity: 'error',
      })
      return
    }
    setFile(selectedFile)
  }

  useEffect(() => {
    API.get('/settings?select=roles').then(
      (res) => res.status === 200 && setRoleOptions(res.data.roles)
    )
  }, [])

  const handleAddNewSubmit = async (e) => {
    e.preventDefault()
    if (existingSOP) {
      await API.put('/sop/' + existingSOP._id, {
        title,
        type,
        version,
        sopNumber,
        date,
        faoRoles,
        faoAllStaff,
        reviewDate,
      }).then((res) => {
        if (res.status === 200) {
          onCloseSubmit({
            title,
            type,
            version,
            sopNumber,
            date,
            faoRoles,
            faoAllStaff,
            reviewDate,
          })
        } else {
          setToast({
            message: 'Unable to edit SOP. Please try again later.',
            severity: 'error',
          })
        }
      })
    } else {
      const fileType = SOP_FILE_TYPES[file?.type]
      if (!fileType) return

      setUploading(true)
      setUploadProgress(0)
      try {
        const upload = await API.post('gets3uploadurl', {
          folder: 'governance/sops',
          extension: fileType.extension,
        })
        if (upload.status !== 200 || !upload.data?.url) {
          throw new Error('Unable to prepare the upload.')
        }

        // Large videos can take minutes, so drop the shared 60s axios
        // timeout; validateStatus never rejects, so check the PUT status or
        // a failed upload would still create the SOP.
        const put = await API.put(upload.data.url, file, {
          headers: {
            'Content-Type': fileType.contentType,
          },
          timeout: 0,
          skipGlobalErrorHandler: true,
          onUploadProgress: (event) => {
            if (event.total) {
              setUploadProgress(Math.round((event.loaded / event.total) * 100))
            }
          },
        })
        if (put.status < 200 || put.status >= 300) {
          throw new Error('The file upload failed. Please try again.')
        }

        const res = await API.post('/new-sop', {
          title,
          type,
          version,
          sopNumber,
          date,
          faoRoles,
          faoAllStaff,
          reviewDate,
          docLink: upload.data.key,
        })
        if (res.status !== 200) {
          throw new Error('Unable to save the SOP. Please try again.')
        }

        loadSOPs()
        onClose()
      } catch (err) {
        console.error(err)
        setToast({
          message: err?.message || 'Unable to save the SOP. Please try again.',
          severity: 'error',
        })
      } finally {
        setUploading(false)
      }
    }
  }

  return (
    <Stack component="form" gap={1} onSubmit={handleAddNewSubmit}>
      <Typography variant="title3">
        {existingSOP ? 'Edit' : 'Add a new'} SOP
      </Typography>
      <TextField
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        label="Title"
        required
      />
      <Autocomplete
        options={['Clinical SOP', 'Operational SOP', 'Clinical Guideline']}
        renderInput={(props) => (
          <TextField
            {...props}
            label="Type"
            helperText="Type inside the box to add a custom Type"
          />
        )}
        value={type}
        onChange={(e, newValue) => setType(newValue || '')}
        inputValue={type}
        onInputChange={(e, newInputValue) => setType(newInputValue)}
        freeSolo
      />
      <TextField
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        fullWidth
        label="Version No"
        required
      />
      <TextField
        value={sopNumber}
        onChange={(e) => setSopNumber(e.target.value)}
        fullWidth
        label="SOP Number"
        required
      />
      <AnDateField
        date={date}
        onChange={(newDate) => setDate(newDate)}
        fullWidth
        label="Date"
        required
      />
      <AnDateField
        date={reviewDate}
        onChange={(newDate) => setReviewDate(newDate)}
        fullWidth
        label="Review Date"
        required
      />
      <Stack direction={{ xs: 'column', lg: 'row' }}>
        <FormControlLabel
          label="All Staff"
          control={
            <Switch
              color="success"
              checked={faoAllStaff}
              onChange={(e) => {
                setFAORoles([])
                setFAOAllStaff(e.target.checked)
              }}
            />
          }
          sx={{ textWrap: 'nowrap' }}
        />
        {faoAllStaff === false && (
          <Autocomplete
            multiple
            options={roleOptions}
            value={faoRoles}
            onChange={(e, value) => setFAORoles(value)}
            fullWidth
            renderInput={(props) => (
              <TextField label="Applicable To" {...props} />
            )}
          />
        )}
      </Stack>
      {!existingSOP && (
        <TextField
          type="file"
          size="small"
          inputProps={{
            accept: Object.keys(SOP_FILE_TYPES).join(','),
          }}
          onChange={handleFileSelect}
          required
        />
      )}
      <Button
        variant="contained"
        size="small"
        color="success"
        type="submit"
        disabled={uploading}
        startIcon={
          uploading ? (
            <CircularProgress
              variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
              size={16}
              thickness={5}
            />
          ) : null
        }
      >
        {uploading ? `Uploading… ${uploadProgress}%` : 'Submit'}
      </Button>
    </Stack>
  )
}

export default AddNewSOP
